import { createRoute } from '@tanstack/react-router';

import { blastByNameQueryOptions, blocksQueryOptions } from '~api/queries';
import { ErrorMessage, Loader } from '~components';
import { queryClient } from '~config';
import { BlastDesignContextPage } from '~features/blast-design-context';
import { trackException } from '~libs';
import { blastsRootRoute } from './blasts';

/**
 * Route for `/blasts/$blastName/design_context` — displays design context for a specific blast.
 */
const blastDesignContextRoute = createRoute({
  getParentRoute: () => blastsRootRoute,
  path: '$blastName/design_context',
  component: BlastDesignContextPage,
  loader: async ({ params }) => {
    const blast = await queryClient.ensureQueryData(blastByNameQueryOptions(params.blastName));
    queryClient.prefetchQuery(blocksQueryOptions({ blast_name: params.blastName }));

    return {
      crumbs: [
        // By using an array, we ensure that the breadcrumb displays: Home / Blasts / $blastName / Blast Design Context
        // The reason is that there's currently no dedicated blast details page,
        // so we want to make sure users can still see the blast name in the breadcrumb when navigating directly to blast design context URL.
        { label: params.blastName, href: '/blasts' },
        { label: 'Blast Design Context', href: `/blasts/${params.blastName}/design_context` },
      ],
      blast,
      blastName: params.blastName,
    };
  },
  pendingComponent: () => (
    <div className="grid h-full w-full place-items-center">
      <Loader orientation="vertical">Loading Blast Design Context...</Loader>
    </div>
  ),
  errorComponent: ({ error }) => {
    trackException(error, { source: 'route.design_context' });

    return (
      <div className="grid h-full w-full place-items-center px-6">
        <ErrorMessage
          className="max-w-sm"
          intent="error"
          title="Error loading Blast Design Context"
          message={error.message}
        />
      </div>
    );
  },
});

export { blastDesignContextRoute };
